import React, { useState } from 'react'
import { PickerFile } from '../useGooglePicker'

type Props = {
  images: PickerFile[]
  isLoading: boolean
  onSelect: (files: PickerFile[]) => void
  onClose: () => void
}

export function BusinessFolderModal({ images, isLoading, onSelect, onClose }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleConfirm = () => {
    const files = images.filter(f => selected.has(f.id))
    onSelect(files)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Browse Business Folder</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Select reference images from your configured Google Drive folder
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="fa fa-times text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <i className="fa fa-spinner fa-spin text-2xl mr-3" />
              Loading folder images...
            </div>
          ) : images.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <i className="fab fa-google-drive text-4xl mb-3" />
              <p className="text-sm">No images found in the configured folder.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {images.map(file => {
                const isSelected = selected.has(file.id)
                return (
                  <button
                    key={file.id}
                    type="button"
                    onClick={() => toggle(file.id)}
                    className={`relative rounded-xl overflow-hidden border-2 transition-all aspect-square bg-gray-100 ${
                      isSelected
                        ? 'border-purple-600 ring-2 ring-purple-300'
                        : 'border-transparent hover:border-purple-300'
                    }`}
                  >
                    {file.thumbnailUrl ? (
                      <img
                        src={file.thumbnailUrl}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">
                        <i className="fa fa-image text-3xl" />
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center shadow">
                        <i className="fa fa-check text-white text-xs" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 px-2 py-1">
                      <p className="text-white text-xs truncate">{file.name}</p>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <p className="text-sm text-gray-500">
            {selected.size > 0 ? `${selected.size} selected` : 'No images selected'}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selected.size === 0}
              className="px-4 py-2 rounded-xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Add Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
